// Wires the inactivity timer to the session store. Call once from
// AppShell at the root of the unlocked routes.

import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  createInactivityTimer,
  createLockBroadcast,
  createVisibilityLockGuard,
} from "../lib/inactivity";
import { useMasterSession } from "../stores/masterSession";
import { useSession } from "../stores/session";
import { useUi } from "../stores/ui";
import { useVault } from "./useVault";

const DEFAULT_TIMEOUT_MS = 5 * 60_000;
const BROADCAST_CHANNEL = "vaultwisp-session";

export function useInactivityLock(timeoutMs = DEFAULT_TIMEOUT_MS): void {
  const session = useSession((s) => s.session);
  const clearSession = useSession((s) => s.clear);
  const showLockedOverlay = useUi((s) => s.showLockedOverlay);
  const vault = useVault();
  const location = useLocation();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!session) return;
    const currentPath = location.pathname + location.search + location.hash;
    const broadcast = createLockBroadcast(BROADCAST_CHANNEL);

    const runLock = (reason: "idle" | "remote") => {
      const current = useSession.getState().session;
      if (!current) return;
      const handle = current.handle;
      clearSession();
      queryClient.clear();
      showLockedOverlay({
        reason: reason === "remote" ? "manual" : "idle",
        returnTo: currentPath,
      });
      vault.lockVault(handle).catch(() => {
        /* best-effort */
      });

      // Also drop the master session. In PR 3 the masterHandle is
      // the same underlying handle as `session.handle`, so the extra
      // `lockVault` call is redundant — `lock_vault` on an already-
      // dropped handle is a documented no-op in vault-wasm. PR 5
      // will decouple these once the master can be a non-OPFS
      // adapter.
      const master = useMasterSession.getState().master;
      if (master) {
        if (master.masterHandle !== handle) {
          vault.lockVault(master.masterHandle).catch(() => {
            /* best-effort */
          });
        }
        useMasterSession.getState().clear();
      }
    };

    const timer = createInactivityTimer({
      timeoutMs,
      onTimeout: () => {
        broadcast.announce();
        runLock("idle");
      },
    });

    const reset = () => timer.reset();
    const events: (keyof WindowEventMap)[] = [
      "mousemove",
      "keydown",
      "pointerdown",
      "wheel",
      "touchstart",
    ];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));

    const guard = createVisibilityLockGuard({
      timeoutMs,
      onLock: () => {
        broadcast.announce();
        runLock("idle");
      },
      onVisible: reset,
    });

    const unsubBroadcast = broadcast.subscribe(() => runLock("remote"));

    timer.start();
    return () => {
      timer.stop();
      events.forEach((e) => window.removeEventListener(e, reset));
      guard.stop();
      unsubBroadcast();
      broadcast.close();
    };
  }, [session, timeoutMs, clearSession, showLockedOverlay, vault, location, queryClient]);
}
